import type {
  CareTeam,
  Extension,
  Practitioner,
  Provenance,
  Questionnaire as FhirQuestionnaireResource,
  QuestionnaireResponse as FhirQuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Task,
} from 'fhir/r4';
import { stripHtml } from 'fhir-sdoh/common/constants';
import moment from 'moment';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CheckboxProps, DropdownProps } from 'semantic-ui-react';
import {
  Checkbox,
  Form,
  FormField,
  Grid,
  Header,
  Icon,
  Loader,
  Message,
  Popup,
} from 'semantic-ui-react';
import {
  QuestionnaireExtensionHelper,
  QuestionnaireHelper,
  QuestionnaireResponseHelper,
} from 'utils/Helpers';
import { v4 as uuid } from 'uuid';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRUpdater from '../../../data/FHIRUpdater';
import { FHIRProxyClient, ProvenanceBuilder } from '../../../fhir-sdoh';
import { parseFHIRCareTeam } from '../../../utils/FHIRUtils';

import {
  ADDENDUM_FIELD_IDS,
  ALL_COSIGN_GROUP_IDS,
  ALL_COSIGN_ATTESTATION_IDS,
  EXT_SUBMITTED_FOR_COSIGN,
} from './constants';
import {
  isAddendumQuestionnaire,
  buildIdentifier,
  appendAddendumExtensions,
  hasNpiIdentifier,
  requiresCosignWorkflow,
  isAwaitingCosign,
  getCosignPractitionerId,
  isSubmittedForCosign,
  findQuestionnaireResponseAnswers,
  getQuestionnairePageItems,
  buildQuestionnaireLinkIdIndex,
  sanitizeQuestionnaireResponseAnswers,
  getInitialSelectedAnswers,
  type LooseQuestionnaireItem,
} from './fhir-helpers';
import type { LooseQuestionnaireResponseItem } from './fhir-helpers';
import QuestionnaireItem from './item';
import { DEFAULT_TODAY_LINK_IDS } from './types/date';
import type {
  QuestionnaireAnswerCell,
  ReferralOptionSource,
  SurveyImageMap,
} from './types/item-props';

/**
 * Props for {@link Questionnaire}. Required vs optional follows what the
 * component can run without, not what callers pass.
 */
export type QuestionnaireProps = {
  allReferralData?: ReferralOptionSource[] | undefined;
  /**
   * The `/CareTeam` search bundle, forwarded unchanged from `patient/index.tsx`
   * where it is SWR's `fhir4.Bundle`. Stated as that bundle rather than as a
   * `{ resource?: CareTeam }` shape: the read site below already asserts the
   * resource type, and declaring the narrower one made this prop disagree with
   * every producer in the chain.
   */
  careTeamData?: fhir4.Bundle | undefined;
  /**
   * Set when this survey is an addendum; the previous link in the chain.
   * `null` from `useAddendumState` — the two reads below truthiness-test it, so
   * null and absent behave alike.
   */
  chainParentId?: string | null | undefined;
  clearAddendumState?: (() => void) | undefined;
  /** The QR being edited; `undefined` for a brand-new response. */
  currentQuestionnaireResponse?: FhirQuestionnaireResponse | undefined;
  /** Drives the "Deleting survey response..." loader in the parent. */
  deleteLoader?: boolean | undefined;
  /** Translation code for the questionnaire's `translation` extensions. */
  language?: string | undefined;
  /** Payer org code, upper/lower-cased into the Provenance agent. */
  org?: string | undefined;
  /** Set when this survey is an addendum; the root chart of the chain. */
  originalChartId?: string | null | undefined;
  patientId: string;
  practitionerData?: { resource?: Practitioner }[] | undefined;
  profileMutate?: (() => void) | undefined;
  /**
   * The survey definition being answered.
   *
   * `id` and `item` are REQUIRED, narrowing the `0..1` cardinality they carry on
   * a FHIR `Questionnaire`: this component reads `questionnaire.item.length`
   * unguarded to size its answer grid, and interpolates `id` into the response's
   * canonical. See the SUSPECTED DEFECT at the call site in `responses.tsx` for
   * what a Questionnaire served without `item` does.
   *
   * Nodes are `LooseQuestionnaireItem` rather than `fhir4.QuestionnaireItem` —
   * the two are not mutually assignable, and every consumer here is written
   * against the loose one.
   */
  questionnaire: FhirQuestionnaireResource & {
    id: string;
    item: LooseQuestionnaireItem[];
  };
  questionnaireResponseData?:
    | { entry?: { resource: FhirQuestionnaireResponse }[] | undefined }
    | undefined;
  refreshPatientDetails?: (() => void) | undefined;
  refreshQA?: ((refresh?: boolean) => void) | undefined;
  /**
   * `responses.tsx`'s `refreshQAForDraftSurveys`. The one call site here passes
   * `null` for `QRID`, which the producer forwards as `setQRID(QRID ?? undefined)`
   * — so `null` and absent behave alike and the parameter admits both.
   *
   * `currentQuestionnaire` is what this component hands back: its own
   * `questionnaire` prop, which is the caller's `Questionnaire` narrowed to
   * require `id` and `item`. Declared as the intersection so the producer —
   * whose parameter is the plain `Questionnaire` — accepts it.
   */
  refreshQAForDraftSurveys?:
    | ((
        activeItem?: string,
        currentQuestionnaire?:
          | (FhirQuestionnaireResource & { id: string; item: LooseQuestionnaireItem[] })
          | null,
        QRID?: string | null,
      ) => Promise<void> | void)
    | undefined;
  refreshTasks?: (() => void) | undefined;
  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. The seven props below are passed by
   * this component's only caller (`responses.tsx`) and read by NOTHING here.
   * `handleLanguageSelect` and `langsList` in particular are the survey-language
   * picker's handler and option list, so the picker they were meant to drive is
   * either gone or never wired — a caregiver cannot switch a survey's language
   * from inside the questionnaire form, only from the response card.
   *
   * Declared rather than removed from the call site: dropping a prop changes
   * what React renders with, and `langsList`/`handleLanguageSelect` may be
   * intended for a control that is still to be added.
   */
  consentDataMutate?: (() => void) | undefined;
  questionnaireResponseMutate?: (() => Promise<fhir4.Bundle | undefined>) | undefined;
  fhirPatient?: unknown;
  handleLanguageSelect?:
    | ((event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => void)
    | undefined;
  langsList?: { key: string; text: string; value: string }[] | undefined;
  setCurrentQuestionnaire?:
    | ((questionnaire: FhirQuestionnaireResource | null | undefined) => void)
    | undefined;
  setCurrentQuestionnaireResponse?:
    | ((response: Partial<FhirQuestionnaireResponse> | undefined) => void)
    | undefined;
  surveyImage?: SurveyImageMap | undefined;
  tasksData?: { entry?: { resource?: Task }[] } | null | undefined;
  tasksMutate?: (() => Promise<unknown> | void) | undefined;
  toggleSurveySidebar?: ((value: unknown) => void) | undefined;
};

function Questionnaire(props: QuestionnaireProps) {
  //props: questionnaire, patientId, toggleSurveySidebar, refreshQA, profileMutate, currentQuestionnaireResponse, org, originalChartId, chainParentId, clearAddendumState
  let currentQRHelper = new QuestionnaireResponseHelper(props.currentQuestionnaireResponse);
  // Safe despite the looser prop shape: the helper only reads `extension` and
  // `item`, both of which are present.
  const QHelper = new QuestionnaireHelper(props.questionnaire);
  const QExtHelper = new QuestionnaireExtensionHelper(QHelper.getSurveyExtension());
  const authInfo = useAuthContext();
  const [answersState, setAnswersState] = useState<QuestionnaireAnswerCell[][]>();
  const answersStateRef = useRef(answersState);
  useEffect(() => {
    answersStateRef.current = answersState;
  }, [answersState]);
  const [_qrResource, setQRResource] = useState<FhirQuestionnaireResponse>();
  const [enableState, setEnableState] = useState<boolean[][]>();
  const [rerender, setRerender] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [error, setError] = useState<string[]>([]);
  const [list, setList] = useState<string[]>([]);
  const [QRLoader, setQRLoader] = useState(false);
  const [obsLoader, setObsLoader] = useState(false);
  const [qrId, setQrId] = useState(currentQRHelper.getId());
  const [_careTeam, setCareTeam] = useState<ReturnType<typeof parseFHIRCareTeam> | null>(null);
  const [requiredState, setRequiredState] = useState<boolean[][]>(); //to track if each question is required
  const [requiredQuestionBorder, setRequiredQuestionBorder] = useState<boolean[][]>(); //use to highlight the required question that doesn't have answer
  const [draftSurveyMessage, setDraftSurveyMessage] = useState<string | null>(null); //show the draft survey message, ex: 'Last saved at 2024-09-01 at 1:30pm'
  const [saveDraftSurvey, setSaveDraftSurvey] = useState(false); //set to true if there is any unsaved updates on the new survey, will not save as a draft survey from a completed survey, we can do it by submit a survey
  const [surveyStatus, setSurveyStatus] = useState<string | null | undefined>(null); //ex: 'completed'
  const [missedQuestions, setMissedQuestions] = useState<
    Array<{ id: string; label: string; categoryLabel: string }>
  >([]); //track missed required questions
  const [submitAttempted, setSubmitAttempted] = useState(false); //track if submit button was clicked
  const [hoveredQuestionIndex, setHoveredQuestionIndex] = useState<number | null>(null); //track which question is hovered
  const [requestCosign, setRequestCosign] = useState(false);

  const currentPractitionerHasNpi = useMemo(() => {
    if (!props.practitionerData || !authInfo.practitionerId) return false;
    // SUSPECTED DEFECT (no user-visible symptom today) — `resource` is FHIR `0..1` and
    // the producer `asResourceEntries` (`utils/bundleNarrowing.ts:81`) is a bare cast that
    // does not filter, so a resource-less entry reaches this `.find` and throws. Upstream
    // reads unguarded (`questionnaires/questionnaire.tsx:71` @ f0be0684) and throws the
    // same TypeError. Asserted, not guarded: a `?.` here would emit a real short-circuit
    // and silently treat the practitioner as having no NPI, changing the cosign gate.
    const me = props.practitionerData.find((p) => p.resource!.id === authInfo.practitionerId);
    return me ? hasNpiIdentifier(me.resource) : false;
  }, [props.practitionerData, authInfo.practitionerId]);

  const isCosignQuestionnaire = requiresCosignWorkflow(props.questionnaire.id);

  const qrIsAwaitingCosign = useMemo(() => {
    const qr = props.currentQuestionnaireResponse;
    if (!qr || qr.status !== 'amended') return false;
    return isAwaitingCosign(qr.item);
  }, [props.currentQuestionnaireResponse]);

  const isAssignedCosigner = useMemo(() => {
    if (!qrIsAwaitingCosign || !currentPractitionerHasNpi) return false;
    const assignedId = getCosignPractitionerId(props.currentQuestionnaireResponse?.item);
    return assignedId === authInfo.practitionerId;
  }, [
    props.currentQuestionnaireResponse,
    qrIsAwaitingCosign,
    currentPractitionerHasNpi,
    authInfo.practitionerId,
  ]);

  const pageQuestions = useMemo(
    () => getQuestionnairePageItems(props.questionnaire),
    [props.questionnaire],
  );

  const questionnaireLinkIdIndex = useMemo(
    () => buildQuestionnaireLinkIdIndex(pageQuestions),
    [pageQuestions],
  );

  useEffect(() => {
    if (qrIsAwaitingCosign && currentPractitionerHasNpi && !isAssignedCosigner) {
      setRequestCosign(true);
    } else {
      setRequestCosign(false);
    }
  }, [props.currentQuestionnaireResponse, props.questionnaire, isAssignedCosigner]);

  const showCosignGroup =
    isCosignQuestionnaire && (!currentPractitionerHasNpi || isAssignedCosigner || requestCosign);

  useEffect(() => {
    // pulling latest tasks data from parent component
    // SUSPECTED DEFECT — `tasksMutate` and the `refresh*`/`profileMutate`
    // callbacks below are called unguarded although every caller passes them
    // with `props?.x` and none is structurally required. Rendering a
    // Questionnaire without them throws "props.tasksMutate is not a function".
    // `questionnaire.spec.tsx`'s `baseProps()` supplies all four, which is what
    // has kept this latent. Asserting preserves the throw.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    (async () => {
      await props.tasksMutate!();
    })();
    currentQRHelper = new QuestionnaireResponseHelper(props.currentQuestionnaireResponse);
    // Each is a page-indexed array of question-indexed cells. The row at `[i]`
    // is created by the loop below before anything reads it, so the row is not
    // optional; only the CELL is. A cell is the question's answer list, or
    // `null` while unanswered.
    const answers: (QuestionnaireResponseItemAnswer[] | null)[][] = [];
    const enable: boolean[][] = [];
    const required: boolean[][] = [];
    const requiredBorder: boolean[][] = [];
    for (let i = 0; i < props.questionnaire.item.length; i++) {
      const answersRow: QuestionnaireAnswerCell[] = [];
      const enableRow: boolean[] = [];
      const requiredRow: boolean[] = [];
      const requiredBorderRow: boolean[] = [];
      answers[i] = answersRow;
      enable[i] = enableRow;
      required[i] = requiredRow;
      requiredBorder[i] = requiredBorderRow;

      const questionsOnPage = pageQuestions[i] ?? [];
      for (let j = 0; j < questionsOnPage.length; j++) {
        const item = questionsOnPage[j]!;
        const isEnabledByDefault = !item?.enableWhen;
        const shouldDefaultToToday =
          item?.type === 'date' &&
          item?.linkId &&
          DEFAULT_TODAY_LINK_IDS.includes(item.linkId) &&
          isEnabledByDefault;
        answersRow[j] = shouldDefaultToToday
          ? [{ valueDate: moment().format('YYYY-MM-DD') }]
          : null;
        const enableResult = item?.enableWhen ? false : true;
        // `includes(undefined)` is `false`, the branch a node without a `linkId`
        // already took.
        if (disabledQuestionLinkId.includes(item.linkId!)) {
          //disable the consent and upload document question from CB survey
          enableRow[j] = false;
          requiredRow[j] = false;
          requiredBorderRow[j] = false;
        } else {
          enableRow[j] = enableResult;
          requiredRow[j] = item?.required && enableResult ? true : false;
          requiredBorderRow[j] =
            item?.required && enableResult && (!answersRow[j] || answersRow[j]?.length === 0)
              ? true
              : false; //we will show the border around the questions that are enabled, required, and have no answer or empty array
        }
        if (
          item.enableWhen &&
          item.enableWhen?.find((enableQuestion) => enableQuestion.question === 'PW-PRACTITIONER')
        ) {
          const practitionerEnable = item.enableWhen?.find(
            (enableQuestion) => enableQuestion.question === 'PW-PRACTITIONER',
          );
          // The enclosing `if` already established that this `find` matches, so
          // `practitionerEnable` is present. `answerCoding` is a different
          // matter: it is optional on the enableWhen entry and dereferenced
          // unguarded, so a `PW-PRACTITIONER` condition served without one
          // THROWS. Asserting keeps that throw.
          if (practitionerEnable!.answerCoding!.code === 'PW-TRUE') {
            enableRow[j] = true;
          } else {
            enableRow[j] = false;
          }
        }
      }
    }
    if (!currentQRHelper.isEmpty()) {
      const applySavedAnswer = (
        linkId: string,
        responseAnswers: QuestionnaireResponseItemAnswer[] | undefined,
      ) => {
        const coords = questionnaireLinkIdIndex.get(linkId);
        if (!coords) return;
        const { pageIndex: x, questionIndex: y } = coords;
        const initialAnswers = getInitialSelectedAnswers(pageQuestions[x]?.[y]);
        if (
          enable[x]![y] &&
          (!responseAnswers || responseAnswers.length === 0) &&
          initialAnswers.length
        ) {
          answers[x]![y] = initialAnswers;
        } else if (responseAnswers?.length) {
          answers[x]![y] = responseAnswers;
        }
      };

      const walkResponseItems = (items: LooseQuestionnaireResponseItem[] | undefined) => {
        for (const node of items ?? []) {
          if (node.item?.length) {
            walkResponseItems(node.item);
          } else if (node.linkId && node.answer?.length) {
            applySavedAnswer(node.linkId, sanitizeQuestionnaireResponseAnswers(node.answer));
          }
        }
      };

      walkResponseItems(currentQRHelper.getItemList());
    }
    setAnswersState(answers);
    setEnableState(enable);
    setRequiredState(required);
    setRequiredQuestionBorder(requiredBorder);
    setQrId(currentQRHelper.getId());
    if (!props.questionnaire) {
      setSurveyStatus(null);
    } else {
      setSurveyStatus(currentQRHelper?.getStatus());
    }
    setSubmit(false);
    setSubmitAttempted(false); // Reset submit attempted when questionnaire changes
    if (currentQRHelper.getStatus() === 'amended') {
      setDraftSurveyMessage(
        `Last saved at ${moment(currentQRHelper?.getLastUpdatedTime()).local().format('YYYY-MM-DD hh:mm a')}`,
      );
    } else {
      setDraftSurveyMessage(null);
    }
    setSaveDraftSurvey(false);
  }, [
    props.currentQuestionnaireResponse,
    props.questionnaire,
    pageQuestions,
    questionnaireLinkIdIndex,
  ]);

  useEffect(() => {
    // `&&` yields `0` when `entry.length` is 0, which is what already reached
    // `parseFHIRCareTeam`; the assertion keeps that rather than adding a guard.
    const careTeamResource =
      props.careTeamData?.entry?.length && props.careTeamData?.entry[0]?.resource;
    setCareTeam(parseFHIRCareTeam(careTeamResource as CareTeam));
  }, [props.careTeamData]);

  useEffect(() => {
    //setSubmit(false);
  }, [props.questionnaire.id]);

  useEffect(() => {
    if (answersState && enableState) {
      updateEnable();
    }
  }, [answersState]);

  const addendumAutoFillApplied = useRef(false);

  useEffect(() => {
    addendumAutoFillApplied.current = false;
  }, [props.questionnaire.id]);

  useEffect(() => {
    if (addendumAutoFillApplied.current) return;
    if (!isAddendumQuestionnaire(props.questionnaire.id)) return;
    if (!answersState || answersState.length === 0) return;

    const updatedAnswers = answersState.map((row) => [...row]);
    let changed = false;

    for (let i = 0; i < pageQuestions.length; i++) {
      const items = pageQuestions[i];
      if (!items?.length) continue;

      for (let j = 0; j < items.length; j++) {
        const linkId = items[j]!.linkId;
        const currentVal = updatedAnswers[i]?.[j];

        if (!currentVal || currentVal.length === 0) {
          if (linkId === ADDENDUM_FIELD_IDS.date || linkId === ADDENDUM_FIELD_IDS.dateSigned) {
            updatedAnswers[i]![j] = [{ valueDate: moment().format('YYYY-MM-DD') }];
            changed = true;
          }
        }
      }
    }

    if (changed) {
      addendumAutoFillApplied.current = true;
      setAnswersState(updatedAnswers);
    }
  }, [props.questionnaire.id, answersState, pageQuestions]);

  // Helper function to get label for questions/categories
  const getQuestionLabel = (q: LooseQuestionnaireItem | null | undefined): string => {
    let displayValue: string | number | undefined;
    if (q?.text) {
      const chosenLang = props.language!;
      const extHelper = new QuestionnaireExtensionHelper(q.extension);
      const translated = extHelper.getTargetTranslation(chosenLang);
      if (translated) {
        displayValue = stripHtml(translated);
      } else {
        displayValue = stripHtml(q.text);
      }
    } else {
      // SUSPECTED DEFECT — `linkId` is optional, and `stripHtml` assigns its
      // argument to `innerHTML`, so a node with neither `text` nor `linkId`
      // renders the literal string "undefined" as its label. Asserting preserves
      // that; the fallback on the return line then cannot mask it because
      // "undefined" is truthy.
      displayValue = stripHtml(q?.linkId as string);
    }
    return String(displayValue || q?.linkId || '');
  };

  // Function to calculate missed questions
  const calculateMissedQuestions = (): Array<{
    id: string;
    label: string;
    categoryLabel: string;
  }> => {
    const missed: Array<{ id: string; label: string; categoryLabel: string }> = [];
    if (requiredState && answersState && enableState && props.questionnaire?.item) {
      for (let i = 0; i < props.questionnaire.item.length; i++) {
        const category = props.questionnaire.item[i]!;
        const isCosignGrp = ALL_COSIGN_GROUP_IDS.includes(category.linkId);
        if (isCosignGrp && !showCosignGroup) continue;
        const questionsOnPage = pageQuestions[i] ?? [];
        for (let j = 0; j < questionsOnPage.length; j++) {
          const question = questionsOnPage[j]!;
          if (
            isCosignGrp &&
            ALL_COSIGN_ATTESTATION_IDS.includes(question.linkId!) &&
            (!currentPractitionerHasNpi || requestCosign)
          )
            continue;
          if (requiredState[i]?.[j] && enableState[i]?.[j] && !answersState[i]?.[j]?.[0]) {
            const questionId = `question-${i}-${j}-${question.linkId}`;
            missed.push({
              id: questionId,
              label: getQuestionLabel(question),
              categoryLabel: getQuestionLabel(category),
            });
          }
        }
      }
    }
    return missed;
  };

  // Update missed questions list whenever answers or required state changes
  useEffect(() => {
    const missed = calculateMissedQuestions();
    setMissedQuestions(missed);
  }, [
    requiredState,
    answersState,
    enableState,
    props.questionnaire,
    props.language,
    pageQuestions,
    showCosignGroup,
  ]);

  // Also recalculate missed questions when error changes or submit is attempted (e.g., after submit fails)
  useEffect(() => {
    if ((error && error.length > 0) || submitAttempted) {
      const missed = calculateMissedQuestions();
      setMissedQuestions(missed);
    }
  }, [error, submitAttempted]);

  //survey warning
  useEffect(() => {
    document.addEventListener('click', handleClick);
    // cleanup function to remove the event listener
    return () => {
      document.removeEventListener('click', handleClick);
    };
  });

  /**
   * Registered with `document.addEventListener('click', …)`, so what actually
   * arrives is the DOM `MouseEvent`, not React's synthetic wrapper — the two
   * were never the same type and the handler simply never said which it took.
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. `target` is `EventTarget | null`,
   * which carries neither `tagName` nor `textContent`; only `Element` does. A
   * click whose target is not an element (the document itself) reads
   * `undefined` for both, and `buttonExclusion.includes(undefined)` is `false`,
   * so such a click is treated as an answer edit and marks the survey dirty.
   */
  const handleClick = (event: MouseEvent) => {
    const target = event.target as Element;
    //we are not setting saveDraftSurvey to true unless it's not a completed survey, saveDraftSurvey is currently false, and it meets event types when answer survey and exclude some button options so we don't save unchanged surveys/completed survey
    if (
      eventTypesForSurveys.includes(target.tagName) &&
      !buttonExclusion.includes(target.textContent) &&
      !saveDraftSurvey &&
      surveyStatus !== 'completed'
    ) {
      setSaveDraftSurvey(true);
    }
  };
  //event types to answer surveys
  const eventTypesForSurveys = ['LABEL', 'INPUT', 'TEXTAREA', 'BUTTON'];
  //buttonExclusion to save draft surveys
  const buttonExclusion = ['Save as draft', 'Submit', 'Exit'];

  // All three come off an `enableWhen` entry, where FHIR leaves each optional.
  // The body only ever compares them, so an absent one simply matches nothing —
  // which is what already happened.
  const getEnable = (
    questionId: string | undefined,
    answerCode: string | undefined,
    operator: string | undefined,
  ) => {
    //To enable practitioner true questions in CB: practitioner enableWhen = true is true, != true is false, = false is false, != false is true
    if (questionId === 'PW-PRACTITIONER') {
      const answerEnable = answerCode === 'PW-TRUE' ? true : false;
      if (operator === '=') {
        if (answerEnable) {
          return true;
        } else {
          return false;
        }
      } else {
        if (answerEnable) {
          return false;
        } else {
          return true;
        }
      }
    }
    if (disabledQuestionLinkId.includes(questionId!)) {
      //disable the consent question from CB survey
      return false;
    }
    const responseAnswers = findQuestionnaireResponseAnswers(
      currentQRHelper.getItemList(),
      questionId!,
    );
    const coords = questionnaireLinkIdIndex.get(questionId!);
    if (coords) {
      const { pageIndex: i, questionIndex: j } = coords;
      const bool = operator === '=' ? false : true;
      const answerItems = answersState?.[i]?.[j]?.length ? answersState[i][j] : responseAnswers;
      if (answerItems) {
        for (let k = 0; k < answerItems.length; k++) {
          const answerItem = answerItems[k];
          if (answerItem?.valueCoding) {
            if (answerItem.valueCoding.code === answerCode) {
              return !bool;
            }
          }
        }
      }
      return bool;
    }
    if (responseAnswers) {
      const bool = operator === '=' ? false : true;
      for (let k = 0; k < responseAnswers.length; k++) {
        if (responseAnswers[k]?.valueCoding?.code === answerCode) {
          return !bool;
        }
      }
      return bool;
    }
    return false;
  };

  const updateEnable = () => {
    let answers = answersState;
    let answersChanged = false;
    for (let i = 0; i < props.questionnaire.item.length; i++) {
      let checkQuestionEnable = true;
      const page = props.questionnaire.item[i]!;
      if (page.enableWhen) {
        let enabled = page.enableBehavior === 'all' ? true : false;
        for (let k = 0; k < page.enableWhen.length; k++) {
          const condition = page.enableWhen[k]!;
          const question = condition.question;
          // `answerCoding` is optional on an enableWhen entry and read unguarded
          // here, so a condition served without one throws. Asserting keeps that.
          const answerCode = condition.answerCoding!.code;
          const operator = condition.operator;
          if (getEnable(question, answerCode, operator) !== enabled) {
            enabled = getEnable(question, answerCode, operator);
            break;
          }
        }
        if (!enabled) {
          if (answers?.[i]?.some((v) => v !== null)) {
            answers = answers?.map((row, ri) => (ri === i ? row.map(() => null) : row)) ?? [];
            answersChanged = true;
          }
          checkQuestionEnable = false;
        }
        const enable = enableState;
        const required = requiredState;
        const requiredBorder = requiredQuestionBorder;
        const questionsOnPage = pageQuestions[i] ?? [];
        for (let a = 0; a < questionsOnPage.length; a++) {
          // `enable`/`required`/`requiredBorder` are the state arrays. The outer
          // `[i]` is indexed without a bound test, so a page index with no row
          // allocated throws on the inner write — that is the existing shape.
          if (disabledQuestionLinkId.includes(questionsOnPage[a]?.linkId as string)) {
            //disable the consent question from CB survey
            enable![i]![a] = false;
            required![i]![a] = false;
            requiredBorder![i]![a] = false;
          } else {
            enable![i]![a] = enabled;
            required![i]![a] = questionsOnPage[a]?.required && enabled ? true : false; //if enabled is true and has required, then we need to set required.
            requiredBorder![i]![a] =
              questionsOnPage[a]?.required &&
              enabled &&
              (!answers?.[i]?.[a] || answers?.[i]?.[a]?.length === 0)
                ? true
                : false; //we will show the border around the questions that are enabled, required, and have no answer or empty array
          }
        }
        setEnableState(enable);
        setRequiredState(required);
        setRequiredQuestionBorder(requiredBorder);
      }
      if (checkQuestionEnable) {
        const questionsOnPage = pageQuestions[i] ?? [];
        for (let j = 0; j < questionsOnPage.length; j++) {
          // In range by the loop bound over `questionsOnPage`.
          const questionDef = questionsOnPage[j]!;
          if (questionDef.enableWhen) {
            let enabled = questionDef.enableBehavior === 'all' ? true : false;
            for (let k = 0; k < questionDef.enableWhen.length; k++) {
              // Likewise in range; `answerCoding` is read unguarded, so an
              // enableWhen clause expressed with any other answer[x] type
              // throws here.
              const question = questionDef.enableWhen[k]!.question;
              const answerCode = questionDef.enableWhen[k]!.answerCoding!.code;
              const operator = questionDef.enableWhen[k]!.operator;
              if (getEnable(question, answerCode, operator) !== enabled) {
                enabled = getEnable(question, answerCode, operator);
                break;
              }
            }
            if (!enabled && answers?.[i]?.[j] != null) {
              answers =
                answers?.map((row, ri) =>
                  ri === i ? [...row.slice(0, j), null, ...row.slice(j + 1)] : row,
                ) ?? [];
              answersChanged = true;
            }
            const enable = enableState;
            const required = requiredState;
            const requiredBorder = requiredQuestionBorder;
            if (disabledQuestionLinkId.includes(questionDef?.linkId as string)) {
              //disable the consent question from CB survey
              enable![i]![j] = false;
              required![i]![j] = false;
              requiredBorder![i]![j] = false;
            } else {
              enable![i]![j] = enabled;
              required![i]![j] = questionDef?.required && enabled ? true : false; //if enabled is true and has required, then we need to set required.
              requiredBorder![i]![j] =
                questionDef?.required &&
                enabled &&
                (!answers?.[i]?.[j] || answers?.[i]?.[j]?.length === 0)
                  ? true
                  : false; //we will show the border around the questions that are enabled, required, and have no answer or empty array
            }
            setEnableState(enable);
            setRequiredState(required);
            setRequiredQuestionBorder(requiredBorder);
          }
        }
      }
      setRerender(!rerender);
    }
    if (answersChanged) setAnswersState(answers);
  };

  const addAnswer = useCallback(
    (answer: QuestionnaireAnswerCell, i: number, j: number) => {
      setAnswersState((prev) => {
        if (prev == null || prev[i] == null) return prev;
        return prev.map((row, ri) =>
          ri === i ? [...row.slice(0, j), answer, ...row.slice(j + 1)] : row,
        );
      });
      setRequiredQuestionBorder((prev) => {
        if (prev == null) return prev;
        return prev.map((row, ri) => {
          if (ri !== i) return row;
          const shouldMark = Boolean(
            (answer === null || answer?.length === 0) && requiredState?.[i]?.[j],
          );
          return [...row.slice(0, j), shouldMark, ...row.slice(j + 1)];
        });
      });
    },
    [requiredState],
  );

  const saveQuestionnaireResponse = (QRId: string, piiSurvey: boolean) => {
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);
    let provenance: Provenance;
    //if PII survey is false, we still use old way to save provenance
    //but if PII survey is true, the questionnaire response Id will not be found in pw fhirstore and will cause error
    //we will remove it and have the QRID in the reason
    if (!piiSurvey) {
      provenance = ProvenanceBuilder.build(
        `QuestionnaireResponse/${QRId}`,
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: authInfo.practitionerName,
        }, //eg. {Practitioner/oa2,johnsmith},
        props.patientId,
        qrId ? 'UPDATE' : 'CREATE',
        `A questionnaire response for ${props.questionnaire.title} has been ${qrId ? 'updated' : 'created'}`,
        // SUSPECTED DEFECT — `org` is optional, but passing the object at all
        // defeats `ProvenanceBuilder.build`'s `{ code: 'HUMANA', display: 'humana' }`
        // default (a default only applies to an *absent* argument). A questionnaire
        // rendered without `org` therefore writes a Provenance whose organization
        // code and display are `undefined`, rather than falling back. Asserting
        // preserves that; the fix is to omit the argument when `org` is absent,
        // which changes what is persisted.
        { code: props.org?.toUpperCase() as string, display: props.org?.toLowerCase() as string }, //eg. {Organization/HUMANA,humana}
      );
    } else {
      provenance = ProvenanceBuilder.build(
        `Patient/${props.patientId}`,
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: authInfo.practitionerName,
        }, //eg. {Practitioner/oa2,johnsmith},
        props.patientId,
        qrId ? 'UPDATE' : 'CREATE',
        `A questionnaire response for ${props.questionnaire.title} has been ${qrId ? 'updated' : 'created'}`,
        // Same defect as the branch above.
        { code: props.org?.toUpperCase() as string, display: props.org?.toLowerCase() as string }, //eg. {Organization/HUMANA,humana}
        QRId,
      );
    }

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {},

      (response) => {
        console.log(response);
        setQRLoader(false);
      },
    );
  };

  //save draft survey
  let autoSaveDraft: ReturnType<typeof setTimeout> | undefined;
  // Function to start the 1 minute timeout to auto save the draft survey
  function startTimeoutForDraftSurvey() {
    // Passed bare: wrapping it in a void arrow would emit a different callback.
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    autoSaveDraft = setTimeout(updateDraftSurveys, 60000);
  }

  // Function to cancel the timeout to auto save the draft survey
  function cancelTimeoutDraftSurvey() {
    clearTimeout(autoSaveDraft);
  }

  //Function to get the consultation task id for linkId PW-OTHER-13
  // eslint-disable-next-line @typescript-eslint/require-await
  async function getAnswerByLinkId(
    questionnaireResponse: { item?: LooseQuestionnaireResponseItem[] } | undefined,
    targetLinkId: string,
  ) {
    const answers = findQuestionnaireResponseAnswers(questionnaireResponse?.item, targetLinkId);
    return answers?.[0]?.valueString ?? null;
  }

  const handleRequestCosignToggle = useCallback(
    (_: FormEvent<HTMLInputElement>, { checked }: CheckboxProps) => {
      setRequestCosign(Boolean(checked));
      if (!checked) {
        setAnswersState((prev) => {
          if (!prev) return prev;
          const updated = prev.map((row) => [...row]);
          for (let i = 0; i < props.questionnaire.item.length; i++) {
            if (ALL_COSIGN_GROUP_IDS.includes(props.questionnaire.item[i]!.linkId)) {
              for (let j = 0; j < (pageQuestions[i]?.length ?? 0); j++) {
                if (updated[i]?.[j] != null) {
                  updated[i]![j] = null;
                }
              }
            }
          }
          return updated;
        });
      }
    },
    [props.questionnaire.item, pageQuestions],
  );

  const handleSubmit = async () => {
    setList([]);
    setSubmit(true);
    setError([]);
    setSubmitAttempted(true); // Mark that submit was attempted
    cancelTimeoutDraftSurvey();
    const questionnaireResponseId = qrId ? qrId : uuid();
    if (!qrId) setQrId(questionnaireResponseId);
    const QRExtensionHelper = new QuestionnaireExtensionHelper(currentQRHelper.getExtension());
    const piiSurvey: boolean = checkPIISurvey(props.questionnaire);
    const needsCosign = isCosignQuestionnaire && (!currentPractitionerHasNpi || requestCosign);
    const submitStatus = needsCosign ? 'amended' : 'completed';

    const questionnaireAnswers: fhir4.QuestionnaireResponse & {
      extension: Extension[];
      item: NonNullable<fhir4.QuestionnaireResponse['item']>;
    } = {
      id: questionnaireResponseId,
      resourceType: 'QuestionnaireResponse',
      questionnaire: `Questionnaire/${props.questionnaire.id}`,
      identifier: buildIdentifier(authInfo.practitionerName),
      status: submitStatus,
      authored: new Date().toISOString(),
      subject: {
        reference: `Patient/${props.patientId}`,
      },
      extension: [
        {
          url: 'https://projectwell.io/fhir/orgid',
          valueString: piiSurvey ? props?.org : 'pw', //pass payor fhir store if it's PII survey
        },
        {
          url: 'https://projectwell.io/fhir/extensions/completed-date',
          //Old responses do not have completed date. In those cases, use the authored field
          valueDateTime: currentQRHelper.getExtension()
            ? QRExtensionHelper.getCompletedDate() //if the completed date is available, use it, otherwise get authoredDate
              ? QRExtensionHelper.getCompletedDate()
              : currentQRHelper.getAuthoredDate() && currentQRHelper.getStatus() === 'completed'
                ? //if no authored field found, create new completed date
                  currentQRHelper.getAuthoredDate()
                : new Date().toISOString()
            : currentQRHelper.getAuthoredDate() && currentQRHelper.getStatus() === 'completed'
              ? //if no authored field found, create new completed date
                currentQRHelper.getAuthoredDate()
              : new Date().toISOString(),
        },
        {
          url: 'https://projectwell.io/fhir/extensions/updated-date',
          valueDateTime: new Date().toISOString(),
        },
      ],
      item: [],
    };
    if (props.originalChartId && props.chainParentId) {
      appendAddendumExtensions(
        questionnaireAnswers.extension,
        props.originalChartId,
        props.chainParentId,
      );
    }
    if (needsCosign) {
      questionnaireAnswers.extension.push({ url: EXT_SUBMITTED_FOR_COSIGN, valueString: 'true' });
    }
    if (currentQRHelper.getQResponse() && qrId) {
      //questionnaireAnswers["id"] = props.currentQuestionnaireResponse.id;
      const existingAuthored = currentQRHelper.getAuthoredDate();
      if (existingAuthored) questionnaireAnswers['authored'] = existingAuthored;
    }
    setQRResource(questionnaireAnswers);
    let validated = true;
    // Calculate and set missed questions when submit is clicked
    const missed = calculateMissedQuestions();
    setMissedQuestions(missed);

    for (let i = 0; i < props.questionnaire.item.length; i++) {
      const isCosignGroup = ALL_COSIGN_GROUP_IDS.includes(props.questionnaire.item[i]!.linkId);
      const skipCosignValidation = isCosignGroup && !showCosignGroup;
      const item: QuestionnaireResponseItem[] = [];
      const questionsOnPage = pageQuestions[i] ?? [];
      for (let j = 0; j < questionsOnPage.length; j++) {
        const questionDef = questionsOnPage[j]!;
        const skipAttestationValidation =
          isCosignGroup &&
          ALL_COSIGN_ATTESTATION_IDS.includes(questionDef.linkId!) &&
          (!currentPractitionerHasNpi || requestCosign);
        if (
          !skipCosignValidation &&
          !skipAttestationValidation &&
          requiredState![i]![j] &&
          !answersState![i]![j]?.[0]
        ) {
          setSubmit(false);
          setError(() => [
            'One or more required (*) questions are not answered, please answer before submitting. You can see the required questions in the warning message above.',
          ]);
          validated = false;
          break;
        }
        item[j] = {
          answer: [],
          linkId: questionDef.linkId!,
        };

        const answersRow = answersState![i];
        if (answersRow) {
          if (answersRow[j]) {
            item[j]!.answer = sanitizeQuestionnaireResponseAnswers(answersRow[j]);
          }
        }
      }

      questionnaireAnswers.item[i] = {
        item: item,
        linkId: props.questionnaire.item[i]!.linkId,
      };
    }

    //if the consultationTaskId exist, add consultation-task-id as an extension
    const consultationTaskId = await getAnswerByLinkId(questionnaireAnswers, 'PW-OTHER-13');
    if (consultationTaskId) {
      questionnaireAnswers.extension.push({
        url: 'https://projectwell.io/fhir/extensions/consultation-task-id',
        valueString: consultationTaskId,
      });
    }

    if (validated) {
      setSurveyStatus('completed');
      setSaveDraftSurvey(false);
      setQRLoader(true);
      await FHIRUpdater(
        `/QuestionnaireResponse/${questionnaireAnswers.id}`,
        authInfo.token,
        questionnaireAnswers,
      ).then(
        () => {
          setList((l) => [...l, 'Survey Response submitted']);
          setSubmit(true);
          // Kept as an expression statement: an `if` would emit different JavaScript.
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          props.toggleSurveySidebar && props.toggleSurveySidebar(null);
          setTimeout(() => {
            // Kept as an expression statement: an `if` would emit different JavaScript.
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            props.refreshQA && props.refreshQA(true);
          }, 6000);
          setTimeout(() => {
            props.refreshTasks!();
            props.profileMutate!();
            props.refreshPatientDetails!();
            setObsLoader(false);
            setQRLoader(false);
            // Kept as an expression statement: an `if` would emit different JavaScript.
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            props.clearAddendumState && props.clearAddendumState();
          }, 11000);
        },
        (error) => {
          setQRLoader(false);
          // Kept as an expression statement: an `if` would emit different JavaScript.
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          questionnaireAnswers && setError((e) => [...e, (error as Error).toString()]);
        },
      );
      //build provenance
      saveQuestionnaireResponse(questionnaireResponseId, piiSurvey);
    }
  };

  const handleLabel = (q: LooseQuestionnaireItem | null | undefined) => {
    let displayValue: string | number | undefined;
    if (q?.text) {
      const chosenLang = props.language!;
      const extHelper = new QuestionnaireExtensionHelper(q.extension);
      const translated = extHelper.getTargetTranslation(chosenLang);
      if (translated) {
        displayValue = stripHtml(translated);
      } else {
        displayValue = stripHtml(q.text);
      }
    } else {
      // Same defect as in `getQuestionLabel` above.
      displayValue = stripHtml(q?.linkId as string);
    }
    return displayValue;
  };

  const scrollToQuestion = (questionId: string) => {
    const element = document.getElementById(questionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a temporary highlight effect
      element.style.transition = 'background-color 0.3s ease';
      element.style.backgroundColor = '#fff3cd';
      setTimeout(() => {
        element.style.backgroundColor = '';
        setTimeout(() => {
          element.style.transition = '';
        }, 300);
      }, 2000);
    }
  };

  // `| undefined` rather than a bare `?:` — under `exactOptionalPropertyTypes`
  // the caller's `extension?: Extension[] | undefined` is not assignable to a
  // property that is merely optional.
  const checkPIISurvey = (questionnaire: { extension?: Extension[] | undefined } | undefined) => {
    if (
      questionnaire?.extension?.find(
        (ext: Extension) => ext.url === 'https://projectwell.io/fhir/contains-pii',
      )?.valueString
    ) {
      return true;
    } else {
      return false;
    }
  };

  // Function to send a PUT request for draft survey responses
  async function updateDraftSurveys() {
    cancelTimeoutDraftSurvey();
    if (surveyStatus === 'completed' || !saveDraftSurvey || !props.questionnaire) {
      return;
    } else {
      setError([]);
      const questionnaireResponseId = qrId ? qrId : uuid();
      if (!qrId) setQrId(questionnaireResponseId);
      const piiSurvey: boolean = checkPIISurvey(props.questionnaire);
      const questionnaireAnswers: fhir4.QuestionnaireResponse & {
        extension: Extension[];
        item: NonNullable<fhir4.QuestionnaireResponse['item']>;
      } = {
        id: questionnaireResponseId,
        resourceType: 'QuestionnaireResponse',
        questionnaire: `Questionnaire/${props.questionnaire.id}`,
        identifier: buildIdentifier(authInfo.practitionerName),
        status: 'amended',
        authored: new Date().toISOString(),
        subject: {
          reference: `Patient/${props.patientId}`,
        },
        extension: [
          {
            url: 'https://projectwell.io/fhir/orgid',
            valueString: piiSurvey ? props?.org : 'pw', //pass payor fhir store if it's PII survey
          },
          {
            url: 'https://projectwell.io/fhir/extensions/updated-date',
            valueDateTime: new Date().toISOString(),
          },
        ],
        item: [],
      };
      if (props.originalChartId && props.chainParentId) {
        appendAddendumExtensions(
          questionnaireAnswers.extension,
          props.originalChartId,
          props.chainParentId,
        );
      }
      if (isSubmittedForCosign(props.currentQuestionnaireResponse?.extension)) {
        questionnaireAnswers.extension.push({ url: EXT_SUBMITTED_FOR_COSIGN, valueString: 'true' });
      }

      if (currentQRHelper.getQResponse() && qrId) {
        //questionnaireAnswers["id"] = props.currentQuestionnaireResponse.id;
        const existingAuthored = currentQRHelper.getAuthoredDate();
        if (existingAuthored) questionnaireAnswers['authored'] = existingAuthored;
      }
      setQRResource(questionnaireAnswers);
      const currentAnswers = answersStateRef.current;
      for (let i = 0; i < props.questionnaire.item.length; i++) {
        const item: QuestionnaireResponseItem[] = [];
        const questionsOnPage = pageQuestions[i] ?? [];
        for (let j = 0; j < questionsOnPage.length; j++) {
          item[j] = {
            answer: [],
            linkId: questionsOnPage[j]!.linkId!,
          };
          const answersRow = currentAnswers![i];
          if (answersRow) {
            if (answersRow[j]) {
              item[j]!.answer = sanitizeQuestionnaireResponseAnswers(answersRow[j]);
            }
          }
        }

        questionnaireAnswers.item[i] = {
          item: item,
          linkId: props.questionnaire.item[i]!.linkId,
        };
      }

      setQRLoader(true);

      await FHIRUpdater(
        `/QuestionnaireResponse/${questionnaireAnswers.id}`,
        authInfo.token,
        questionnaireAnswers,
      ).then(
        (data) => {
          setSaveDraftSurvey(false);
          setDraftSurveyMessage(
            `Last saved at ${moment(data?.meta?.lastUpdated).local().format('YYYY-MM-DD hh:mm a')}`,
          );
          setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/no-floating-promises -- fire-and-forget refresh behind a short-circuit; a rejection only leaves the draft survey stale
            props?.refreshQAForDraftSurveys &&
              props?.refreshQAForDraftSurveys(questionnaireAnswers.id, props.questionnaire, null);
            setQRLoader(false);
          }, 6000);
        },
        (error) => {
          setQRLoader(false);
          // Kept as an expression statement: an `if` would emit different JavaScript.
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          questionnaireAnswers && setError((e) => [...e, (error as Error).toString()]);
        },
      );
    }
  }

  useEffect(() => {
    // Set up the interval to send data every minute
    if (saveDraftSurvey && surveyStatus !== 'completed' && props.questionnaire) {
      startTimeoutForDraftSurvey();
    } else if (surveyStatus === 'completed' || !props.questionnaire) {
      cancelTimeoutDraftSurvey();
      setSaveDraftSurvey(false);
    }

    // Cleanup the timeout if the saveDraftSurvey/surveyStatus updates before auto save draft survey
    return () => clearTimeout(autoSaveDraft);
  }, [saveDraftSurvey, surveyStatus]);

  // Check if indicator should show
  const hasMissedQuestions =
    missedQuestions && Array.isArray(missedQuestions) && missedQuestions.length > 0;
  const hasRequiredError =
    error &&
    Array.isArray(error) &&
    error.length > 0 &&
    typeof error[0] === 'string' &&
    error[0].toLowerCase().includes('required');
  // Show indicator if submit was attempted and there are missed questions, or if there's a required error
  const shouldShowIndicator = (submitAttempted && hasMissedQuestions) || hasRequiredError;

  return (
    <div
      style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '80vh', position: 'relative' }}
    >
      {props?.deleteLoader && (
        <Loader active inline="centered" content={'Deleting survey response...'} />
      )}
      <Form success={submit} error={error.length > 0}>
        <Header as="h3">{QExtHelper.getInternalTitle() ?? props.questionnaire.title}</Header>
        {props.questionnaire.item.map((category: LooseQuestionnaireItem, i: number) => {
          if (ALL_COSIGN_GROUP_IDS.includes(category.linkId!) && !showCosignGroup) return null;

          return (
            <Form.Group key={i.toString()} grouped>
              <FormField
                inline
                className={
                  enableState && category?.required ? (enableState?.[i] ? 'required' : '') : ''
                }
              >
                <Icon color="blue" name="folder open outline" />
                {/* we will show the border around the questions that are enabled, required, and have no answer or empty
                array */}
                <label
                  style={
                    category?.required && requiredQuestionBorder?.[i]
                      ? { border: '1px solid red' }
                      : undefined
                  }
                >
                  {handleLabel(category)}
                </label>
              </FormField>
              {(pageQuestions[i] ?? []).map((question: LooseQuestionnaireItem, j: number) => {
                if (
                  ALL_COSIGN_GROUP_IDS.includes(category.linkId!) &&
                  ALL_COSIGN_ATTESTATION_IDS.includes(question.linkId!) &&
                  (!currentPractitionerHasNpi || requestCosign)
                )
                  return null;

                const questionId = `question-${i}-${j}-${question.linkId}`;
                return (
                  <div key={i.toString() + j.toString()} id={questionId}>
                    <FormField
                      inline
                      className={
                        enableState && question?.required
                          ? enableState?.[i]?.[j]
                            ? 'required'
                            : ''
                          : ''
                      }
                    >
                      {/*highlight the label when the question type is a display(prompt), the enable is true, and has text */}
                      {question?.type === 'display' &&
                      question?.text &&
                      (enableState ? (enableState[i] ? enableState[i][j] : null) : null) ? (
                        <label style={{ border: '2px solid green' }}>
                          {' '}
                          <Icon color="orange" name="file alternate outline" />
                          {handleLabel(question)}
                        </label>
                      ) : question?.type === 'display' ||
                        !(enableState ? (enableState[i] ? enableState[i][j] : null) : null) ? (
                        //show red stop icon when the display/question is disabled
                        <>
                          <label>
                            <Icon color="red" name="ban" />
                            {handleLabel(question)}
                          </label>
                        </>
                      ) : (
                        <>
                          {/* we will show the border around the questions that are enabled, required, and have no answer or
                      empty array */}
                          <label
                            style={
                              question?.required &&
                              requiredQuestionBorder?.[i]?.[j] &&
                              (!answersState?.[i]?.[j] || answersState?.[i]?.[j].length === 0)
                                ? { border: '1px solid red' }
                                : undefined
                            }
                          >
                            {' '}
                            <Icon color="orange" name="file alternate outline" />
                            {handleLabel(question)}
                          </label>
                        </>
                      )}
                    </FormField>

                    <QuestionnaireItem
                      question={question}
                      addAnswer={addAnswer}
                      i={i}
                      j={j}
                      currentAnswer={
                        answersState ? (answersState[i] ? answersState[i][j] : null) : null
                      }
                      enable={enableState ? (enableState[i] ? enableState[i][j] : null) : null}
                      answersStateReady={answersState != null && answersState.length > 0}
                      language={props.language}
                      surveyImage={props?.surveyImage}
                      allReferralData={props.allReferralData}
                      practitionerData={props.practitionerData}
                      tasksData={props?.tasksData}
                      surveyStatus={surveyStatus}
                      questionnaireResponseData={props?.questionnaireResponseData}
                      currentQrId={qrId}
                      excludePractitionerId={requestCosign ? authInfo.practitionerId : undefined}
                      disableCosignPractitioner={isAssignedCosigner}
                    />
                    <br />
                  </div>
                );
              })}
            </Form.Group>
          );
        })}
        {submit && <Message success header="Success" list={list} />}
        {(QRLoader || obsLoader) && (
          <Message icon>
            <Icon name="circle notched" loading />
            <Message.Content>
              <Message.Header>Loading</Message.Header>
              {QRLoader && 'Submitting Survey Response...'}
              {obsLoader && `Submitting Profile Labels...`}
            </Message.Content>
          </Message>
        )}

        {/* Missed Required Questions Indicator */}
        {shouldShowIndicator && (
          <div style={{ width: '100%', display: 'block' }}>
            <Message
              warning
              style={{
                marginBottom: '1rem',
                marginTop: '1rem',
                display: 'block',
                visibility: 'visible',
                opacity: 1,
              }}
            >
              <Message.Header>
                <Icon name="exclamation triangle" color="orange" />
                {missedQuestions && missedQuestions.length > 0
                  ? `${missedQuestions.length} Required ${
                      missedQuestions.length === 1 ? 'Question' : 'Questions'
                    } Missed`
                  : 'Required Questions Missed'}
              </Message.Header>
              {missedQuestions && missedQuestions.length > 0 ? (
                <Message.Content>
                  <Message.List>
                    {missedQuestions.map((missed, index) => (
                      <Message.Item
                        key={index}
                        style={{
                          cursor: 'pointer',
                          color: '#856404',
                          textDecoration: hoveredQuestionIndex === index ? 'underline' : 'none',
                        }}
                        onMouseEnter={() => setHoveredQuestionIndex(index)}
                        onMouseLeave={() => setHoveredQuestionIndex(null)}
                        onClick={() => scrollToQuestion(missed.id)}
                      >
                        <strong>{missed.categoryLabel}</strong>: {missed.label}
                      </Message.Item>
                    ))}
                  </Message.List>
                </Message.Content>
              ) : (
                <Message.Content>
                  <p>
                    Please scroll through the form to find and answer all required questions marked
                    with (*).
                  </p>
                </Message.Content>
              )}
            </Message>
          </div>
        )}

        {error && <Message error header="Error" list={error} />}

        {isCosignQuestionnaire && currentPractitionerHasNpi && !isAssignedCosigner && (
          <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
            <Checkbox
              toggle
              label="Request Co-Sign"
              checked={requestCosign}
              onChange={handleRequestCosignToggle}
            />
          </div>
        )}

        <Grid divided="vertically">
          <Grid.Row columns={4}>
            {<Grid.Column width={1} />}
            {/* <Grid.Column width={6}> */}
            <Grid.Column>
              <Form.Button
                color="blue"
                content={
                  isCosignQuestionnaire && (!currentPractitionerHasNpi || requestCosign)
                    ? 'Submit for Co-Sign'
                    : 'Submit'
                }
                onClick={handleSubmit}
                disabled={QRLoader || obsLoader}
              />
            </Grid.Column>
            {currentQRHelper.getStatus() !== 'completed' && (
              <Grid.Column>
                {/* <Grid.Column width={6}> */}
                <Form.Button
                  color="orange"
                  content="Save as draft"
                  onClick={updateDraftSurveys}
                  disabled={QRLoader || obsLoader}
                />
                <Message.Content>{draftSurveyMessage && draftSurveyMessage}</Message.Content>
              </Grid.Column>
            )}
            {props.toggleSurveySidebar && (
              // <Grid.Column width={4}>
              <Grid.Column>
                <Popup
                  content="Please submit before exiting to save the survey response!"
                  trigger={
                    <Form.Button
                      color="red"
                      content="Exit"
                      onClick={() => props.toggleSurveySidebar!(null)}
                      disabled={QRLoader || obsLoader}
                    />
                  }
                />
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Form>
    </div>
  );
}

export default Questionnaire;

//below has the disabled question linkId for CB, such as consent, personal information
const disabledQuestionLinkId = [
  'FOR_WEB_SURVEY_ONLY_CLICK_WRAP_CONSENT_AGREEMENT',
  'PW-FOOD-DLVRY-14a',
  'PW-FOOD-DLVRY-14b',
  'PW-FOOD-DLVRY-15a',
  'PW-FOOD-DLVRY-15b',
  'PW-FOOD-DLVRY-15c',
  'PW-FOOD-DLVRY-15d',
  'PW-FOOD-DLVRY-15e',
  'PW-PERSONAL-01',
  'PW-PERSONAL-02',
  'PW-PERSONAL-03',
  'PW-PERSONAL-04',
  'DELIVERY_ADDRESS_INTRO',
];
